import React, { useMemo } from 'react';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useProjectElementListStore } from '../../../shared/store/projectElementStore';
import SelectBox from '../../../shared/component/SelectBox';
import { DocumentData } from '../../../shared/dto/EntityRepository';
import { SelectType, DisplaySize, DisplayAlignment, ImageStyle, PEInfoType, normalizePEInfoType, DetailType } from '../../../shared/enum/EnumRepository';
import { useImageStyleJudge } from '../../../shared/hooks/useImageStyleJudge';
import DocumentInfo from '../DocumentInfo';
import DocumentImgInPeMobile from './DocumentImgInPeMobile';
import DocumentVideoInPeMobile from './DocumentVideoInPeMobile';
import { PEImgContentWrapper, PEHeader, PEIndicator, SelectBoxContainer, SelectBoxWrapper, ImgContentCoreWrapper, ImgWrapper } from './WorkMobile';

export interface DocumentMobileProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  infoType?: PEInfoType;
  data: DocumentData;
  priority: boolean;
}

const DocumentMobile: React.FC<DocumentMobileProps> = ({ peId, alignment, displaySize, infoType, data, priority }) => {
  const { isEditMode } = useEditMode();
  const handleDisplaySizeChange = useProjectElementListStore((s) => s.updateDisplaySize);
  const handleInfoTypeChange = useProjectElementListStore((s) => s.updateInfoType);
  const handleChange = useProjectElementListStore((s) => s.updateDocument);
  const { judgeImageStyle } = useImageStyleJudge();
  const imageStyle = useMemo(() => judgeImageStyle(data.uploadFile?.width / data.uploadFile?.height), [data.uploadFile?.width, data.uploadFile?.height]);
  const effectiveInfoType = normalizePEInfoType(infoType);
  const detailType = data.detailType ?? DetailType.IMAGE;

  return (
    <PEImgContentWrapper id={`project-element-document-${peId}`}>
      {isEditMode &&
        <PEHeader>
          <SelectBoxContainer>
            <SelectBoxWrapper>
              <SelectBox
                value={displaySize || DisplaySize.BIG}
                selectType={SelectType.WORK_SIZE}
                handleChange={(value) => handleDisplaySizeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
            <SelectBoxWrapper>
              <SelectBox
                value={effectiveInfoType}
                selectType={SelectType.PE_INFO_TYPE}
                handleChange={(value) => handleInfoTypeChange(peId, value)}
                direction={false} />
            </SelectBoxWrapper>
          </SelectBoxContainer>
          <PEIndicator peId={peId}>: Visual Aid</PEIndicator>
        </PEHeader>
      }
      <ImgContentCoreWrapper>
        <ImgWrapper>
          {detailType === DetailType.VIDEO ? (
            <DocumentVideoInPeMobile
              peId={peId}
              alignment={DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.REGULAR}
              data={data}
              priority={priority}
              onChangeType={() => handleChange(peId, { detailType: DetailType.IMAGE })}
              onVideoUrlChange={(videoUrl) => handleChange(peId, { videoUrl })}
            />
          ) : (
            <DocumentImgInPeMobile
              peId={peId}
              alignment={DisplayAlignment.CENTER}
              displaySize={displaySize || DisplaySize.REGULAR}
              data={data}
              priority={priority}
              onChangeType={() => handleChange(peId, { detailType: DetailType.VIDEO })}
            />
          )}
        </ImgWrapper>
        <DocumentInfo
          peId={peId}
          alignment={alignment || DisplayAlignment.CENTER}
          displaySize={displaySize || DisplaySize.REGULAR}
          imageStyle={imageStyle || ImageStyle.LANDSCAPE}
          infoType={effectiveInfoType}
          data={data} />
      </ImgContentCoreWrapper>
    </PEImgContentWrapper>
  );
};

export default DocumentMobile;
